import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import {
  GetBrokerageAddressDetails,
  GetManagingBrokerDetails,
} from "services/broker";
import {
  OFFICE_NOT_LISTED,
  MANAGING_BROKER_NOT_LISTED,
  OFFICE_NOT_LISTED_LABEL,
  MANAGING_BROKER_NOT_LISTED_LABEL,
  formatAddressLine,
  getOfficeAddressDisplayParts,
  getOfficeOptionName,
  isManagingBrokerNotListed,
  isOfficeNotListed,
  mapOfficeToBrokerageFields,
} from "views/AgentDetails/utils/brokerage";
import styles from "./styles";

const DropdownOptionLabel = ({ classes, primary, secondary }) => (
  <div>
    <Typography variant="body2" className={classes.dropdownOptionPrimary}>
      {primary}
    </Typography>
    {secondary ? (
      <Typography variant="caption" className={classes.dropdownOptionSecondary}>
        {secondary}
      </Typography>
    ) : null}
  </div>
);

const BrokerageOfficeFields = ({
  classes,
  values,
  errors = {},
  countyID,
  brokerageName,
  disabled = false,
  layout = "stacked",
  onValuesChange,
}) => {
  const [brokerageOfficeOptions, setBrokerageOfficeOptions] = React.useState([]);
  const [managingBrokerOptions, setManagingBrokerOptions] = React.useState([]);
  const [isOfficeDataLoading, setIsOfficeDataLoading] = React.useState(false);
  const [isManagingBrokerDataLoading, setIsManagingBrokerDataLoading] =
    React.useState(false);
  const [officeDataError, setOfficeDataError] = React.useState(null);
  const [managingBrokerDataError, setManagingBrokerDataError] =
    React.useState(null);

  React.useEffect(() => {
    if (!countyID || !brokerageName) {
      setBrokerageOfficeOptions([]);
      setManagingBrokerOptions([]);
      return;
    }

    let cancelled = false;
    setIsOfficeDataLoading(true);
    setIsManagingBrokerDataLoading(true);
    setOfficeDataError(null);
    setManagingBrokerDataError(null);

    Promise.all([
      GetBrokerageAddressDetails(countyID, brokerageName),
      GetManagingBrokerDetails(countyID, brokerageName),
    ])
      .then(([offices, brokers]) => {
        if (cancelled) return;
        setBrokerageOfficeOptions(offices || []);
        setManagingBrokerOptions(brokers || []);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err || "Unable to load brokerage details.";
        setOfficeDataError(message);
        setManagingBrokerDataError(message);
        setBrokerageOfficeOptions([]);
        setManagingBrokerOptions([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsOfficeDataLoading(false);
        setIsManagingBrokerDataLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [countyID, brokerageName]);

  const showOffice =
    values.brokerageOfficeID &&
    !isOfficeNotListed(values.brokerageOfficeID);
  const selectedOffice = showOffice
    ? brokerageOfficeOptions.find(
        (office) =>
          String(office.officeID) === String(values.brokerageOfficeID)
      )
    : null;
  const officeAddressParts = getOfficeAddressDisplayParts(selectedOffice);
  const displayBrokerageName =
    values.brokerageName || brokerageName || "";

  const handleOfficeChange = (event) => {
    const { value } = event.target;

    if (isOfficeNotListed(value)) {
      onValuesChange({ brokerageOfficeID: OFFICE_NOT_LISTED });
      return;
    }

    const office = brokerageOfficeOptions.find(
      (item) => String(item.officeID) === String(value)
    );

    onValuesChange({
      ...(office ? mapOfficeToBrokerageFields(office) : { brokerageOfficeID: value }),
    });
  };

  const handleManagingBrokerChange = (event) => {
    const { value } = event.target;

    if (isManagingBrokerNotListed(value)) {
      onValuesChange({
        managingBrokerID: MANAGING_BROKER_NOT_LISTED,
        managingBrokerName: MANAGING_BROKER_NOT_LISTED,
      });
      return;
    }

    const broker = managingBrokerOptions.find(
      (item) => String(item.managingBrokerID) === String(value)
    );

    onValuesChange({
      managingBrokerID: value,
      managingBrokerName: broker ? broker.managingBrokerName : "",
    });
  };

  const renderOfficeValue = (selected) => {
    if (!selected) {
      return (
        <Typography variant="body2" className={classes.selectPlaceholder}>
          Select Your Brokerage Office Location
        </Typography>
      );
    }
    if (isOfficeNotListed(selected)) {
      return (
        <Typography variant="body2" className={classes.notListedOption}>
          {OFFICE_NOT_LISTED_LABEL}
        </Typography>
      );
    }
    const office = brokerageOfficeOptions.find(
      (item) => String(item.officeID) === String(selected)
    );
    if (!office) return null;
    return (
      <DropdownOptionLabel
        classes={classes}
        primary={getOfficeOptionName(office)}
        secondary={formatAddressLine(office)}
      />
    );
  };

  const renderManagingBrokerValue = (selected) => {
    if (!selected) {
      return (
        <Typography variant="body2" className={classes.selectPlaceholder}>
          Select Your Managing Broker
        </Typography>
      );
    }
    if (isManagingBrokerNotListed(selected)) {
      return (
        <Typography variant="body2" className={classes.notListedOption}>
          {MANAGING_BROKER_NOT_LISTED_LABEL}
        </Typography>
      );
    }
    const broker = managingBrokerOptions.find(
      (item) => String(item.managingBrokerID) === String(selected)
    );
    if (!broker) return null;
    return (
      <DropdownOptionLabel
        classes={classes}
        primary={broker.managingBrokerName}
        secondary={formatAddressLine(broker)}
      />
    );
  };

  const officeError = errors.brokerageOfficeID;
  const licenseError = errors.brokerLicenseNumber;
  const managingBrokerError = errors.managingBrokerID;

  const officeField = (
    <>
      <TextField
        select
        className={classes.textField}
        label="Office Location"
        size="small"
        variant="outlined"
        required
        name="brokerageOfficeID"
        value={values.brokerageOfficeID || null}
        InputLabelProps={{ shrink: true }}
        error={Boolean(officeError)}
        disabled={disabled || isOfficeDataLoading}
        helperText={
          isOfficeDataLoading
            ? "Loading office locations..."
            : officeDataError
            ? officeDataError
            : !brokerageOfficeOptions.length && !isOfficeDataLoading
            ? "No office locations found."
            : ""
        }
        onChange={handleOfficeChange}
        SelectProps={{
          displayEmpty: true,
          renderValue: renderOfficeValue,
          MenuProps: { className: classes.menu },
        }}
      >
        {brokerageOfficeOptions.map((office) => (
          <MenuItem key={office.officeID} value={String(office.officeID)}>
            <DropdownOptionLabel
              classes={classes}
              primary={getOfficeOptionName(office)}
              secondary={formatAddressLine(office)}
            />
          </MenuItem>
        ))}
        <MenuItem value={OFFICE_NOT_LISTED}>
          <Typography
            variant="body2"
            className={classNames(
              classes.dropdownOptionPrimary,
              classes.notListedOption
            )}
          >
            {OFFICE_NOT_LISTED_LABEL}
          </Typography>
        </MenuItem>
      </TextField>
      {officeError && (
        <Typography className={classes.fieldError} variant="body2">
          {officeError[0]}
        </Typography>
      )}
    </>
  );

  const licenseField = showOffice ? (
    <>
      <TextField
        className={classes.textField}
        label="Brokerage License Number"
        size="small"
        variant="outlined"
        name="brokerLicenseNumber"
        value={values.brokerLicenseNumber || ""}
        error={Boolean(licenseError)}
        disabled={disabled}
        onChange={(event) =>
          onValuesChange({ brokerLicenseNumber: event.target.value })
        }
      />
      {licenseError && (
        <Typography className={classes.fieldError} variant="body2">
          {licenseError[0]}
        </Typography>
      )}
    </>
  ) : null;

  const managingBrokerField = (
    <>
      <TextField
        select
        className={classes.textField}
        label="Managing Broker"
        size="small"
        variant="outlined"
        required
        name="managingBrokerID"
        value={values.managingBrokerID || null}
        InputLabelProps={{ shrink: true }}
        error={Boolean(managingBrokerError)}
        disabled={disabled || isManagingBrokerDataLoading}
        helperText={
          isManagingBrokerDataLoading
            ? "Loading managing brokers..."
            : managingBrokerDataError
            ? managingBrokerDataError
            : !managingBrokerOptions.length && !isManagingBrokerDataLoading
            ? "No managing brokers found."
            : ""
        }
        onChange={handleManagingBrokerChange}
        SelectProps={{
          displayEmpty: true,
          renderValue: renderManagingBrokerValue,
          MenuProps: { className: classes.menu },
        }}
      >
        {managingBrokerOptions.map((broker) => (
          <MenuItem
            key={broker.managingBrokerID}
            value={String(broker.managingBrokerID)}
          >
            <DropdownOptionLabel
              classes={classes}
              primary={broker.managingBrokerName}
              secondary={formatAddressLine(broker)}
            />
          </MenuItem>
        ))}
        <MenuItem value={MANAGING_BROKER_NOT_LISTED}>
          <Typography
            variant="body2"
            className={classNames(
              classes.dropdownOptionPrimary,
              classes.notListedOption
            )}
          >
            {MANAGING_BROKER_NOT_LISTED_LABEL}
          </Typography>
        </MenuItem>
      </TextField>
      {managingBrokerError && (
        <Typography className={classes.fieldError} variant="body2">
          {managingBrokerError[0]}
        </Typography>
      )}
    </>
  );

  if (layout === "horizontal") {
    return (
      <Grid container spacing={0}>
        <Grid item lg={4} md={12} xs={12}>
          <div className={classes.horizontalField}>{officeField}</div>
        </Grid>
        <Grid item lg={4} md={12} xs={12}>
          <div className={classes.horizontalField}>{licenseField}</div>
        </Grid>
        <Grid item lg={4} md={12} xs={12}>
          <div className={classes.horizontalField}>{managingBrokerField}</div>
        </Grid>
      </Grid>
    );
  }

  if (layout === "split") {
    return (
      <Grid container spacing={0}>
        <Grid item lg={6} md={12} xs={12}>
          <div className={classes.splitColumn}>
            {officeField}
            {licenseField}
            {managingBrokerField}
          </div>
        </Grid>
        <Grid item lg={6} md={12} xs={12}>
          <div className={classes.splitColumn}>
            <TextField
              className={classes.textField}
              label="Brokerage Name"
              size="small"
              variant="outlined"
              name="brokerageName"
              value={displayBrokerageName}
              inputProps={{ readOnly: true }}
            />
            {showOffice && officeAddressParts.length > 0 && (
              <div className={classes.brokerageAddressField}>
                {officeAddressParts.map((line, index) => (
                  <Typography
                    key={`office-address-${index}`}
                    variant="subtitle2"
                    className={classes.brokerageAddressLine}
                  >
                    {line}
                  </Typography>
                ))}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      {officeField}
      {licenseField}
      {managingBrokerField}
    </>
  );
};

BrokerageOfficeFields.propTypes = {
  classes: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  countyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  brokerageName: PropTypes.string,
  disabled: PropTypes.bool,
  layout: PropTypes.oneOf(["stacked", "horizontal", "split"]),
  onValuesChange: PropTypes.func.isRequired,
};

export default withStyles(styles)(BrokerageOfficeFields);
